import React, { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { layers } from '../data/lessons';
import { useProgress } from '../hooks/useProgress';
import './Lesson.css';

const allLessons = layers[0].lessons;

function InflationChart() {
  const bars = [
    { label: 'Savings account (3.5%)', value: 141, color: '#6F6A5E', width: 47 },
    { label: 'Fixed deposit (7%)', value: 197, color: '#B68D40', width: 66 },
    { label: 'Inflation (6%)', value: 179, color: '#8E2B2B', width: 60, isInflation: true },
    { label: 'Index fund (12%)', value: 311, color: '#3B6D2A', width: 100 },
  ];
  return (
    <div className="viz-box">
      <div className="viz-title">₹1,00,000 invested for 10 years — what it becomes</div>
      <div className="viz-subtitle">The red line shows what you need just to keep up with inflation</div>
      {bars.map((b, i) => (
        <div key={i} className="bar-row">
          <div className="bar-label">{b.label}</div>
          <div className="bar-track">
            <div
              className={`bar-fill ${b.isInflation ? 'bar-inflation' : ''}`}
              style={{ width: `${b.width}%`, background: b.color }}
            >
              <span className="bar-val">₹{b.value}K</span>
            </div>
          </div>
        </div>
      ))}
      <div className="viz-note">At consistent annual returns. Past performance is indicative, not guaranteed.</div>
    </div>
  );
}

function CompoundChart() {
  const bars = [
    { label: 'Start at 22 → retire at 60', value: '₹3.5 Cr', width: 100, color: '#3B6D2A' },
    { label: 'Start at 27 → retire at 60', value: '₹1.9 Cr', width: 54, color: '#B68D40' },
    { label: 'Start at 32 → retire at 60', value: '₹1.1 Cr', width: 31, color: '#7A5A15' },
  ];
  return (
    <div className="viz-box">
      <div className="viz-title">₹1,000/month invested at 12% — when you start matters</div>
      {bars.map((b, i) => (
        <div key={i} className="bar-row">
          <div className="bar-label">{b.label}</div>
          <div className="bar-track">
            <div className="bar-fill" style={{ width: `${b.width}%`, background: b.color }}>
              <span className="bar-val">{b.value}</span>
            </div>
          </div>
        </div>
      ))}
      <div className="viz-note">Same ₹1,000/month, same 12% returns. Only the start date changes.</div>
    </div>
  );
}

function RiskChart() {
  const items = [
    { label: 'Savings account', risk: 'Zero risk', ret: '3.5%', riskW: 2, retW: 8, riskC: '#3B6D2A', retC: '#6F6A5E' },
    { label: 'Fixed deposit', risk: 'Very low', ret: '6–7%', riskW: 10, retW: 25, riskC: '#B68D40', retC: '#B68D40' },
    { label: 'Debt mutual fund', risk: 'Low', ret: '7–8%', riskW: 18, retW: 30, riskC: '#7A5A15', retC: '#7A5A15' },
    { label: 'Index fund (Nifty 50)', risk: 'Medium', ret: '10–12%', riskW: 40, retW: 60, riskC: '#8E2B2B', retC: '#3B6D2A' },
    { label: 'Individual stocks', risk: 'High', ret: '0–50%+', riskW: 85, retW: 90, riskC: '#8E2B2B', retC: '#8E2B2B' },
  ];
  return (
    <div className="viz-box">
      <div className="viz-title">Risk vs return — the spectrum</div>
      <div className="risk-legend">
        <span><span className="legend-dot" style={{background:'#8E2B2B'}}></span>Risk level</span>
        <span><span className="legend-dot" style={{background:'#3B6D2A'}}></span>Potential return</span>
      </div>
      {items.map((item, i) => (
        <div key={i} className="risk-row">
          <div className="risk-label">{item.label}</div>
          <div className="risk-bars">
            <div className="mini-track">
              <div className="mini-fill" style={{width:`${item.riskW}%`, background: item.riskC}}></div>
            </div>
            <div className="mini-track">
              <div className="mini-fill" style={{width:`${item.retW}%`, background: item.retC}}></div>
            </div>
          </div>
          <div className="risk-ret">{item.ret}</div>
        </div>
      ))}
    </div>
  );
}

function EmergencyChart() {
  const months = [15000, 15000, 15000, 15000, 15000, 15000];
  return (
    <div className="viz-box">
      <div className="viz-title">If your monthly expenses are ₹15,000</div>
      <div className="emergency-grid">
        <div className="em-card em-min">
          <div className="em-label">Minimum (3 months)</div>
          <div className="em-amount">₹45,000</div>
          <div className="em-desc">Covers job loss, medical emergency for 3 months</div>
        </div>
        <div className="em-card em-ideal">
          <div className="em-label">Ideal (6 months)</div>
          <div className="em-amount">₹90,000</div>
          <div className="em-desc">Full security — sleep easy no matter what happens</div>
        </div>
      </div>
      <div className="em-months">
        {months.map((m, i) => (
          <div key={i} className={`em-month ${i < 3 ? 'em-filled-min' : 'em-filled-ideal'}`}>
            <div className="em-month-label">Month {i + 1}</div>
            <div className="em-month-val">₹{m.toLocaleString()}</div>
          </div>
        ))}
      </div>
      <div className="viz-note">Keep this in a savings account or liquid mutual fund — accessible within 24 hours.</div>
    </div>
  );
}

function getChart(lessonId) {
  if (lessonId === 2) return <CompoundChart />;
  if (lessonId === 3) return <InflationChart />;
  if (lessonId === 4) return <RiskChart />;
  if (lessonId === 5) return <EmergencyChart />;
  return null;
}

export default function Lesson() {
  const { id } = useParams();
  const navigate = useNavigate();
  const lessonId = parseInt(id);
  const lesson = allLessons.find(l => l.id === lessonId);
  const { isCompleted, completeLesson } = useProgress();

  const [quizStarted, setQuizStarted] = useState(false);
  const [answers, setAnswers] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [score, setScore] = useState(0);

  if (!lesson) {
    return (
      <div className="lesson-not-found">
        <h2>Topic not found</h2>
        <Link to="/">Go back home</Link>
      </div>
    );
  }

  const nextLesson = allLessons.find(l => l.id === lessonId + 1);
  const alreadyDone = isCompleted(lessonId);

  const handleAnswer = (qId, optionIndex) => {
    if (submitted) return;
    setAnswers(prev => ({ ...prev, [qId]: optionIndex }));
  };

  const handleSubmit = () => {
    let correct = 0;
    lesson.quiz.forEach(q => {
      if (answers[q.id] === q.correct) correct++;
    });
    const pct = Math.round((correct / lesson.quiz.length) * 100);
    setScore(pct);
    setSubmitted(true);
    completeLesson(lessonId, pct);
  };

  const allAnswered = lesson.quiz.every(q => answers[q.id] !== undefined);

  return (
    <div className="lesson-page">

      <div className="lesson-topbar">
        <div className="lesson-topbar-inner">
          <Link to="/topics" className="back-link">← Topics</Link>
          <div className="lesson-meta-top">
            <span className="layer-badge">Financial guide</span>
          </div>
        </div>
      </div>

      <div className="lesson-inner">

        <div className="lesson-header">
          <div className="lesson-dots">
            <span>{lesson.duration} min read</span>
            <span className="ldot"></span>
            <span>{lesson.quiz.length} quiz questions</span>
            {alreadyDone && <span className="done-badge">Completed</span>}
          </div>
          <h1 className="lesson-title">{lesson.title}</h1>
          <p className="lesson-intro">{lesson.content.intro}</p>
        </div>

        <div className="lesson-body">
          {lesson.content.sections.map((section, i) => (
            <div key={i} className="content-section">
              <h2 className="section-heading">{section.heading}</h2>
              <p className="section-body">{section.body}</p>
            </div>
          ))}

          {getChart(lesson.id)}

          <div className="example-block">
            <div className="example-label">REAL EXAMPLE</div>
            <p className="example-text">{lesson.content.example}</p>
          </div>

          <div className="takeaway-block">
            <div className="takeaway-label">KEY TAKEAWAY</div>
            <p className="takeaway-text">{lesson.content.keyTakeaway}</p>
          </div>

          {!quizStarted ? (
            <div className="quiz-start-card">
              <div className="quiz-start-title">Ready to test yourself?</div>
              <p className="quiz-start-sub">{lesson.quiz.length} questions · takes about 2 minutes</p>
              <button className="quiz-start-btn" onClick={() => setQuizStarted(true)}>
                Start quiz →
              </button>
            </div>
          ) : (
            <div className="quiz-section">
              <div className="quiz-header">Quiz — {lesson.title}</div>

              {lesson.quiz.map((q, qi) => (
                <div key={q.id} className="quiz-question">
                  <div className="q-number">Question {qi + 1} of {lesson.quiz.length}</div>
                  <div className="q-text">{q.question}</div>
                  <div className="q-options">
                    {q.options.map((opt, oi) => {
                      let cls = 'q-option';
                      if (submitted) {
                        if (oi === q.correct) cls += ' correct';
                        else if (answers[q.id] === oi && oi !== q.correct) cls += ' wrong';
                      } else if (answers[q.id] === oi) {
                        cls += ' selected';
                      }
                      return (
                        <div
                          key={oi}
                          className={cls}
                          onClick={() => handleAnswer(q.id, oi)}
                        >
                          <span className="q-option-letter">{String.fromCharCode(65 + oi)}</span>
                          {opt}
                        </div>
                      );
                    })}
                  </div>
                  {submitted && (
                    <div className="q-explanation">
                      <span className="explanation-label">Why: </span>
                      {q.explanation}
                    </div>
                  )}
                </div>
              ))}

              {!submitted ? (
                <button
                  className={`submit-btn ${allAnswered ? 'active' : 'disabled'}`}
                  onClick={allAnswered ? handleSubmit : undefined}
                >
                  {allAnswered ? 'Submit answers →' : `Answer all ${lesson.quiz.length} questions to submit`}
                </button>
              ) : (
                <div className="result-card">
                  <div className="result-score">{score}%</div>
                  <div className="result-label">
                    {score === 100 ? 'Perfect score!' : score >= 66 ? 'Well done!' : 'Good effort — review the explanations above'}
                  </div>
                  <p className="result-sub">
                    You got {lesson.quiz.filter(q => answers[q.id] === q.correct).length} of {lesson.quiz.length} correct
                  </p>
                  {nextLesson ? (
                    <button className="next-btn" onClick={() => navigate(`/topic/${nextLesson.id}`)}>
                      Next topic: {nextLesson.title} →
                    </button>
                  ) : (
                    <button className="next-btn" onClick={() => navigate('/')}>
                      Back to home →
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
